import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PublicContentView from '../app/layout/PublicContentView';
import PublicFooter from '../components/PublicFooter';
import AttachmentToolbar from '../editor/controls/toolbar/AttachmentToolbar';
import EmbedToolbar from '../editor/controls/toolbar/EmbedToolbar';
import Gutter from '../editor/Gutter';
import EditorModal from '../editor/modals/EditorModal';
import QuillComponent from '../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import NotFoundView from '../error/NotFoundView';
import POST_CONTENT_FRAGMENT from '../graphql/fragments/PostContent';
import type {
  PublicPostViewQuery,
  PublicPostViewQueryVariables,
} from '../graphql/generated/graphqlTypes';
import UIActions from '../ui/UIActions';
import useSlug from '../utils/router/useSlug';
import PostActionButtons from './actionButtons/PostActionButtons';
import PostSeriesBar from './PostSeriesBar';
import PostSkeleton from './PostSkeleton';
import PostStickyControl from './PostStickyControl';
import PostViewBanner from './PostViewBanner';
import styles from './PublicPostView.module.scss';
import PostSeriesFooter from './series/PostSeriesFooter';
import PostTopicListWithBreadcrumbs from './topic/PostTopicListWithBreadcrumbs';

const EDITOR_MODULE_CONFIG = {
  headerCollapse: true,
  scrollToAnchor: true,
  hoveredBlotListener: true,
};

const PUBLIC_POST_FIELDS_FRAGMENT = gql`
  fragment PublicPostFields on PublicPost {
    id
    title
    ...PostContent
    series {
      id
      name
      posts {
        id
        title
      }
      ...PostSeriesBar_series
    }
    banner {
      original
    }
    topics {
      id
      name
      privacy
      ancestors {
        id
        name
      }
    }
  }
  ${POST_CONTENT_FRAGMENT}
  ${PostSeriesBar.fragments.series}
`;

const PUBLIC_POST_QUERY = gql`
  query PublicPostView($id: ID!) {
    post: publicPost(id: $id) {
      ...PublicPostFields
      ...PublicContentView_post
      ...AttachmentToolbar_publicPost
    }
  }
  ${PUBLIC_POST_FIELDS_FRAGMENT}
  ${PublicContentView.fragments.post}
  ${AttachmentToolbar.fragments.publicPost}
`;

const PublicPostView = () => {
  const [id] = useSlug('postSlug');
  const { data: { post } = {}, loading } = useQuery<
    PublicPostViewQuery,
    PublicPostViewQueryVariables
  >(PUBLIC_POST_QUERY, {
    variables: { id },
  });
  const dispatch = useDispatch();
  const { quill } = useQuill();
  const [isStickyTOCOpen, setIsStickyTOCOpen] = useState<boolean | undefined>();

  const postLayoutContainerRef = useRef(null);

  useEffect(() => {
    if (quill && isStickyTOCOpen === undefined) {
      const hasHeaders = !!quill.root.querySelector('h1, h2, h3');
      setIsStickyTOCOpen(hasHeaders);
    }
  }, [quill, isStickyTOCOpen]);

  useEffect(() => {
    const { hash } = document.location;
    if (hash) {
      const header = document.getElementById(hash.slice(1));
      if (header != null) {
        header.scrollIntoView();
      }
    }
  }, [post]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLImageElement;
    if (target.tagName === 'IMG') {
      dispatch(
        UIActions.openModal(EditorModal, { image: { src: target.src } }),
      );
    }
  };

  if (!post) {
    return loading ? null : <NotFoundView />;
  }

  const currentBannerUrl = post.banner?.original;
  const hasBanner = currentBannerUrl != null;

  return (
    <PublicContentView
      className={classNames(
        'public-post post-container no-padding-x no-padding-y',
        { 'show-sticky-toc': isStickyTOCOpen, 'has-banner': hasBanner },
      )}
      post={post}
    >
      <div
        ref={postLayoutContainerRef}
        className="post-layout-container post-topic-breadcrumb-enabled"
      >
        {post ? (
          <WiderLineSizeContext
            containerRef={postLayoutContainerRef}
            editor={quill ? quill.scroll.domNode : null}
            isStickyTOCOpen={isStickyTOCOpen || false}
          >
            {currentBannerUrl && (
              <div className="post-layout-banner">
                <PostViewBanner banner={currentBannerUrl} />
              </div>
            )}
            <div className="post-layout-above">
              {/* TODO: Fix postTopics type to support public topics when not editing */}
              {/* @ts-expect-error */}
              <PostTopicListWithBreadcrumbs postTopics={post.topics} />
              {post.series && (
                <PostSeriesBar postId={post.id} series={post.series} />
              )}
            </div>
            <PostStickyControl
              postId={post.id}
              tocClassName="post-layout-toc"
              actionButtonsClassName="post-layout-actions"
              isStickyTOCOpen={isStickyTOCOpen || false}
              onToggleStickyTOC={() => setIsStickyTOCOpen(!isStickyTOCOpen)}
            />
            <div
              className="post-layout-editor"
              onDoubleClick={handleDoubleClick}
            >
              <div className="editor-container">
                {quill && (
                  <>
                    <EmbedToolbar publicPost />
                    <AttachmentToolbar publicPost post={post} />
                  </>
                )}
                <QuillComponent
                  namespace="public"
                  content={post.content}
                  rootClassName={classNames(
                    'post-editor',
                    styles.publicPostEditor,
                  )}
                  moduleConfig={EDITOR_MODULE_CONFIG}
                  readOnly
                />
                {quill && <Gutter quill={quill} readOnly />}
              </div>
            </div>
            <div className="post-layout-below">
              {post.series && (
                <div className={styles.postSeriesBarContainer}>
                  <PostSeriesFooter postId={post.id} series={post.series} />
                  <PostSeriesBar postId={post.id} series={post.series} />
                </div>
              )}
              <div className={styles.footerContainer}>
                <PublicFooter />
              </div>
            </div>
          </WiderLineSizeContext>
        ) : (
          <div className="post-layout-editor">
            <div className="post-layout-above" />
            <div className="post-layout-editor-inner">
              <PostSkeleton />
            </div>
          </div>
        )}
      </div>
      <div className="post-footer">
        <PostActionButtons horizontal postId={id} />
      </div>
    </PublicContentView>
  );
};

export default withQuillContextProvider(PublicPostView);
